import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, Download, Smartphone, Share, Plus, MoreVertical } from 'lucide-react';

const DISMISS_KEY = 'pwa_dismissed';

// Detect platform
function detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    return { isIos, isAndroid, isStandalone };
}

export const PWAInstallPrompt = () => {
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [showIosGuide, setShowIosGuide] = useState(false);
    const [showAndroidGuide, setShowAndroidGuide] = useState(false);
    const [platform, setPlatform] = useState({ isIos: false, isAndroid: false, isStandalone: false });

    useEffect(() => {
        const p = detectPlatform();
        setPlatform(p);
        // Already installed → don't show
        if (p.isStandalone) return;
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed) return;

        // Android/Chrome: listen to beforeinstallprompt
        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // iOS: show banner manually after delay
        if (p.isIos) {
            const t = setTimeout(() => setShow(true), 3000);
            return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handler); };
        }
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const install = async () => {
        if (platform.isIos) {
            setShowIosGuide(true);
            return;
        }
        if (deferred) {
            deferred.prompt();
            const result = await deferred.userChoice;
            if (result.outcome === 'accepted') {
                setShow(false);
                setDeferred(null);
            }
        } else {
            // Fallback: show generic guide
            setShowAndroidGuide(true);
        }
    };

    const dismiss = () => {
        setShow(false);
        localStorage.setItem(DISMISS_KEY, '1');
    };

    if (!show) {
        return (
            <>
                <IosInstallGuide open={showIosGuide} onOpenChange={setShowIosGuide} />
                <AndroidInstallGuide open={showAndroidGuide} onOpenChange={setShowAndroidGuide} />
            </>
        );
    }

    return (
        <>
            <div className="fixed bottom-20 lg:bottom-4 left-4 right-4 z-50 max-w-md mx-auto bg-[hsl(220_13%_10%)] text-white rounded-xl shadow-2xl p-4 flex items-center gap-3 border border-[hsl(var(--accent))]/40" data-testid="pwa-install-prompt">
                <div className="w-10 h-10 bg-[hsl(var(--accent))] rounded flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-[hsl(220_13%_10%)]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">Installa CantiereAI</div>
                    <div className="text-xs text-white/70">{platform.isIos ? 'Apri come app dal tuo iPhone' : 'Accedi veloce dalla schermata Home'}</div>
                </div>
                <Button onClick={install} size="sm" className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold" data-testid="pwa-install-button">
                    {platform.isIos ? 'Come?' : 'Installa'}
                </Button>
                <button onClick={dismiss} className="text-white/60 p-1" data-testid="pwa-dismiss" aria-label="Chiudi"><X className="w-4 h-4" /></button>
            </div>

            <IosInstallGuide open={showIosGuide} onOpenChange={setShowIosGuide} />
            <AndroidInstallGuide open={showAndroidGuide} onOpenChange={setShowAndroidGuide} />
        </>
    );
};


function IosInstallGuide({ open, onOpenChange }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="ios-install-guide">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Smartphone className="w-5 h-5 text-[hsl(var(--accent))]" /> INSTALLA SU iPHONE</DialogTitle></DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">In 3 passi installi CantiereAI come app sul tuo iPhone. <strong>Funziona solo da Safari</strong>, non da Chrome iOS.</p>
                <ol className="space-y-3 mt-2">
                    <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold flex items-center justify-center text-sm flex-shrink-0">1</span>
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                            <span>Tocca il pulsante <strong>Condividi</strong></span>
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[hsl(var(--muted))] border"><Share className="w-4 h-4" /></span>
                            <span>in basso al centro di Safari</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold flex items-center justify-center text-sm flex-shrink-0">2</span>
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                            <span>Scorri e seleziona <strong>Aggiungi alla schermata Home</strong></span>
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[hsl(var(--muted))] border"><Plus className="w-4 h-4" /></span>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold flex items-center justify-center text-sm flex-shrink-0">3</span>
                        <div className="text-sm"><strong>Aggiungi</strong> → CantiereAI appare come app nella tua home. ✓</div>
                    </li>
                </ol>
                <Button onClick={() => onOpenChange(false)} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold h-11 mt-3">Ho capito</Button>
            </DialogContent>
        </Dialog>
    );
}

function AndroidInstallGuide({ open, onOpenChange }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="android-install-guide">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Smartphone className="w-5 h-5 text-[hsl(var(--accent))]" /> INSTALLA SU ANDROID</DialogTitle></DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">In 2 passi installi CantiereAI come app sul tuo telefono Android (Chrome).</p>
                <ol className="space-y-3 mt-2">
                    <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold flex items-center justify-center text-sm flex-shrink-0">1</span>
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                            <span>Tocca i 3 pallini</span>
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[hsl(var(--muted))] border"><MoreVertical className="w-4 h-4" /></span>
                            <span>in alto a destra in Chrome</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold flex items-center justify-center text-sm flex-shrink-0">2</span>
                        <div className="text-sm">Seleziona <strong>Installa app</strong> oppure <strong>Aggiungi alla schermata Home</strong></div>
                    </li>
                </ol>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Se non vedi l'opzione, la app è già installata oppure il browser non supporta PWA.</p>
                <Button onClick={() => onOpenChange(false)} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold h-11 mt-3">Ho capito</Button>
            </DialogContent>
        </Dialog>
    );
}
